"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "@/hooks/localization/useTranslations";
import useIsRtl from "@/hooks/useIsRtl";

// Fraction of the visible width moved per button press — keeps a sliver of
// the previous items on screen so users don't lose their place.
const SCROLL_STEP = 0.8;

type Props = { children: React.ReactNode };

// Horizontal overflow for the top-level mega nav: shows prev/next buttons only
// when items are hidden, since a mouse wheel can't scroll a horizontal strip.
const MegaNavScroller: React.FC<Props> = ({ children }) => {
  const t = useTranslation();
  const isRtl = useIsRtl();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // scrollLeft is 0 at the start and negative toward the end in RTL.
    const offset = Math.abs(el.scrollLeft);
    setCanScrollStart(offset > 1);
    setCanScrollEnd(offset + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener("scroll", updateEdges, { passive: true });
    const observer = new ResizeObserver(updateEdges);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateEdges);
      observer.disconnect();
    };
  }, [updateEdges]);

  const scrollByStep = (towardEnd: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    const physicalSign = (towardEnd ? 1 : -1) * (isRtl ? -1 : 1);
    el.scrollBy({
      left: physicalSign * el.clientWidth * SCROLL_STEP,
      behavior: "smooth",
    });
  };

  const chevron = (pointsLeft: boolean) => (
    <svg
      viewBox="0 0 6 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d={pointsLeft ? "M5 1L1 5l4 4" : "M1 1l4 4-4 4"} />
    </svg>
  );

  return (
    <>
      <button
        type="button"
        className="mega-nav-scroll-btn"
        aria-label={t.previous}
        hidden={!canScrollStart}
        onClick={() => scrollByStep(false)}
      >
        {chevron(!isRtl)}
      </button>
      <div ref={scrollRef} className="mega-nav-scroll">
        {children}
      </div>
      <button
        type="button"
        className="mega-nav-scroll-btn"
        aria-label={t.next}
        hidden={!canScrollEnd}
        onClick={() => scrollByStep(true)}
      >
        {chevron(isRtl)}
      </button>
    </>
  );
};

export default MegaNavScroller;
